import { Badge } from '@octopus-do/octopus-storybook'
import cx from 'classnames'
import type React from 'react'
import { forwardRef } from 'react'
import { Link } from 'react-router-dom'

import css from './Menu.module.scss'

type Props = {
  children: React.ReactNode
  isDanger?: boolean
  disabled?: boolean
  isLink?: boolean
  url?: string
  rel?: string
  target?: string
  badge?: string
  alignLeft?: boolean
  notAdvanced?: boolean
  noHover?: boolean
  active?: boolean
  customClassName?: string
  onClick?: React.MouseEventHandler<HTMLDivElement | HTMLAnchorElement>
  onProBadgeClick?:
    | React.MouseEventHandler<HTMLDivElement | HTMLAnchorElement>
    | undefined
    | null
}

type ContentProps = Pick<
  Props,
  'disabled' | 'notAdvanced' | 'badge' | 'onProBadgeClick'
> &
  React.PropsWithChildren

const Content: React.FC<ContentProps> = ({
  disabled,
  notAdvanced,
  badge,
  onProBadgeClick,
  children,
}) => (
  <>
    {disabled ? <div className={css.label}>{children}</div> : children}

    {notAdvanced && (
      <div className={css.badge_pro} onClick={(e) => onProBadgeClick?.(e)}>
        <Badge type="pro" size="large" />
      </div>
    )}
    {badge !== '' && (
      <div className={css.badge} onClick={(e) => onProBadgeClick?.(e)}>
        {badge}
      </div>
    )}
  </>
)

const Menu = forwardRef<HTMLAnchorElement | HTMLDivElement, Props>(
  (
    {
      children,
      isDanger = false,
      isLink = false,
      noHover = false,
      url = '',
      rel,
      target = '_self',
      disabled,
      alignLeft = false,
      active,
      customClassName,
      onClick,
      notAdvanced,
      onProBadgeClick,
      badge = '',
    },
    ref
  ) => {
    const handleOnClick = (
      event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>
    ) => {
      if (disabled) {
        event.preventDefault()
        return
      }

      onClick?.(event)
    }

    if (isLink) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={cx(css.menu, css.link, customClassName, {
            [css.danger]: isDanger,
            [css.disabled]: disabled,
            [css.alignLeft]: alignLeft,
            [css.noHover]: noHover,
          })}
          rel={rel || undefined}
          to={url}
          target={target}
          onClick={handleOnClick}
        >
          <Content
            disabled={disabled}
            notAdvanced={notAdvanced}
            badge={badge}
            onProBadgeClick={onProBadgeClick}
          >
            {children}
          </Content>
        </Link>
      )
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={cx(css.menu, customClassName, {
          [css.danger]: isDanger,
          [css.disabled]: disabled,
          [css.alignLeft]: alignLeft,
          [css.noHover]: noHover,
          [css.active]: active,
        })}
        onClick={handleOnClick}
      >
        <Content
          disabled={disabled}
          notAdvanced={notAdvanced}
          badge={badge}
          onProBadgeClick={onProBadgeClick}
        >
          {children}
        </Content>
      </div>
    )
  }
)

export default Menu
